import { ScreenNoiseRenderer } from './ScreenNoiseRenderer';
import { grainDevicePx, nextPhase, type Size } from './screenNoiseMath';

/** A tile's tuning, read on every frame. `grain` is in CSS px. */
export interface TileParams {
  strength: number;
  grain: number;
}

/** One protected picture on the page: a 2D canvas the compositor paints into. */
export interface NoiseTile {
  /** The picture to show; uploaded while the tile is visible. Throws when the browser refuses its pixels. */
  setImage(img: HTMLImageElement): void;
  /** The tile canvas's drawing buffer, in device px. */
  setSize(size: Size): void;
  /** Off-screen tiles neither animate nor hold a texture. */
  setVisible(visible: boolean): void;
  dispose(): void;
  readonly maxSize: number;
}

export interface Compositor {
  /** `onLost`: the shared context is gone — show the plain image from now on. */
  addTile(canvas: HTMLCanvasElement, params: () => TileParams, onLost: () => void): NoiseTile;
}

export type RendererLike = Pick<
  ScreenNoiseRenderer,
  'canvas' | 'maxSize' | 'upload' | 'release' | 'fit' | 'render' | 'dispose'
>;

export interface CompositorEnv {
  raf: (cb: FrameRequestCallback) => number;
  caf: (id: number) => void;
  matchMedia: (query: string) => MediaQueryList;
  dpr: () => number;
}

interface TileState {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  params: () => TileParams;
  onLost: () => void;
  image: HTMLImageElement | null;
  texture: WebGLTexture | null;
  size: Size | null;
  visible: boolean;
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const browserEnv = (): CompositorEnv => ({
  raf: (cb) => requestAnimationFrame(cb),
  caf: (id) => cancelAnimationFrame(id),
  matchMedia: (query) => window.matchMedia(query),
  dpr: () => window.devicePixelRatio || 1,
});

/**
 * Screen noise for every protected picture on the page through ONE WebGL
 * context (browsers keep only ~16 alive and drop the oldest, so a context per
 * picture would leak plain images in a large album). One frame loop and one
 * phase: each frame renders every visible tile's texture into the shared
 * canvas and copies it into the tile's own 2D canvas. The loop runs only
 * while something visible can animate; under prefers-reduced-motion each
 * tile is drawn once per change instead (a static grainy frame). The shared
 * canvas is kept as small as the largest visible tile, and off-screen tiles
 * shrink their own canvas to 1×1 (iOS caps total canvas memory).
 */
export const createCompositor = (
  renderer: RendererLike,
  env: CompositorEnv = browserEnv(),
): Compositor & { lose(): void } => {
  const tiles = new Set<TileState>();
  const motion = env.matchMedia(REDUCED_MOTION);
  let phase = 0;
  let frame = 0;
  let lost = false;

  const drawTile = (tile: TileState) => {
    if (lost || !tile.visible || !tile.texture || !tile.size) return;
    const { strength, grain } = tile.params();
    renderer.render(tile.texture, tile.size, phase, strength, grainDevicePx(grain, env.dpr()));
    // WebGL draws into the canvas's bottom-left corner.
    const { width, height } = tile.size;
    tile.ctx.drawImage(renderer.canvas, 0, renderer.canvas.height - height, width, height, 0, 0, width, height);
  };

  const animating = () =>
    !lost && !motion.matches && [...tiles].some((tile) => tile.visible && tile.texture && tile.size);

  // The shared canvas as small as the largest visible tile: every copy out of it costs its full size.
  const fitShared = () => {
    let width = 1;
    let height = 1;
    tiles.forEach((tile) => {
      if (!tile.visible || !tile.size) return;
      width = Math.max(width, tile.size.width);
      height = Math.max(height, tile.size.height);
    });
    renderer.fit({ width, height });
  };

  const tick = () => {
    frame = 0;
    phase = nextPhase(phase);
    fitShared();
    tiles.forEach(drawTile);
    schedule();
  };
  const schedule = () => {
    if (!frame && animating()) frame = env.raf(tick);
  };
  const sync = () => {
    if (frame && !animating()) {
      env.caf(frame);
      frame = 0;
    }
    schedule();
  };
  motion.addEventListener('change', () => {
    if (motion.matches) tiles.forEach(drawTile);
    sync();
  });

  const releaseTexture = (tile: TileState) => {
    if (tile.texture) renderer.release(tile.texture);
    tile.texture = null;
  };
  const upload = (tile: TileState) => {
    releaseTexture(tile);
    if (tile.image) tile.texture = renderer.upload(tile.image);
  };
  // The tile canvas's buffer: its size while visible, 1×1 (no memory) off screen.
  const applySize = (tile: TileState) => {
    const width = tile.visible && tile.size ? tile.size.width : 1;
    const height = tile.visible && tile.size ? tile.size.height : 1;
    if (tile.canvas.width !== width) tile.canvas.width = width;
    if (tile.canvas.height !== height) tile.canvas.height = height;
  };

  return {
    addTile(canvas, params, onLost) {
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        // No 2D context (iOS: canvas memory exhausted): this tile shows its plain image.
        onLost();
        return { maxSize: renderer.maxSize, setImage() {}, setSize() {}, setVisible() {}, dispose() {} };
      }
      const tile: TileState = {
        canvas,
        ctx,
        params,
        onLost,
        image: null,
        texture: null,
        size: null,
        visible: false,
      };
      tiles.add(tile);
      applySize(tile);
      return {
        maxSize: renderer.maxSize,
        setImage(img) {
          tile.image = img;
          if (tile.visible) {
            upload(tile);
            drawTile(tile);
          }
          sync();
        },
        setSize(size) {
          tile.size = size;
          applySize(tile);
          drawTile(tile);
          sync();
        },
        setVisible(visible) {
          if (tile.visible === visible) return;
          tile.visible = visible;
          applySize(tile);
          if (visible) {
            try {
              upload(tile);
            } catch {
              // Pixels refused (e.g. SecurityError): this tile shows its plain image.
              tiles.delete(tile);
              tile.onLost();
              return;
            }
            drawTile(tile);
          } else {
            releaseTexture(tile);
          }
          sync();
        },
        dispose() {
          releaseTexture(tile);
          tiles.delete(tile);
          sync();
        },
      };
    },
    lose() {
      lost = true;
      if (frame) env.caf(frame);
      frame = 0;
      const all = [...tiles];
      tiles.clear();
      all.forEach((tile) => tile.onLost());
    },
  };
};

// The page-wide compositor: created on first use, `null` (for good) where
// WebGL is unusable; recreated on the next use after a context loss.
let instance: Compositor | null | undefined;

export const getCompositor = (): Compositor | null => {
  if (instance !== undefined) return instance;
  try {
    const canvas = document.createElement('canvas');
    const renderer = ScreenNoiseRenderer.create(canvas);
    if (!renderer) {
      instance = null;
      return instance;
    }
    const compositor = createCompositor(renderer);
    canvas.addEventListener('webglcontextlost', () => {
      // Tiles on screen fall back to their plain image; the next picture
      // mounted gets a fresh context.
      instance = undefined;
      compositor.lose();
    });
    instance = compositor;
  } catch {
    instance = null;
  }
  return instance;
};
